import { useState, useEffect, useRef } from "react"
import AssetsGrid from "./AssetsGrid/AssetsGrid";

import { fetchUserProducts, fetchPurchasedProducts, clearUserProducts } from "../store/slices/profileProductSlice";
import { confirmPayment } from "../store/slices/paymentSlice";
import { fetchCart, clearCart, setPaymentClearing } from "../store/slices/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import BASE_URL from "../config";
import { fetchWithAuth } from "../utils/authUtils";
import { readErrorResponse } from "../utils/readErrorResponse";

const extractErrorMessage = (errorDetails) => {
	if (typeof errorDetails === "string") {
		return errorDetails;
	}
	if (typeof errorDetails?.error === "string") {
		return errorDetails.error;
	}
	if (typeof errorDetails?.detail === "string") {
		return errorDetails.detail;
	}
	if (typeof errorDetails?.message === "string") {
		return errorDetails.message;
	}
	if (errorDetails && typeof errorDetails === "object") {
		const firstStringValue = Object.values(errorDetails).find((value) => typeof value === "string");
		if (firstStringValue) {
			return firstStringValue;
		}
	}
	return "Failed to load products.";
};


const ProfileAssets = ({ userId = null, showPurchases = true }) => {
	const dispatch = useDispatch()
	const {userProducts, purchasedProducts} = useSelector(state=> state.profileProduct)
	const [searchParams, setSearchParams] = useSearchParams();

	const [activeCategory, setActiveCategory] = useState(userId && !showPurchases ? 'assets' : 'purchases');
	// Храним все ассеты автора (для client-side пагинации в Assets)
	const [allAuthorAssets, setAllAuthorAssets] = useState(null);
	const [allAssetsLoading, setAllAssetsLoading] = useState(false);
	const [allAssetsError, setAllAssetsError] = useState(null);

	// Очищаем стейлые userProducts при монтировании, чтобы не показывать чужие продукты
	useEffect(() => {
		dispatch(clearUserProducts());
	}, [dispatch]);
	const [pollingPayment, setPollingPayment] = useState(false);
	const pollingRef = useRef(null);
	const initialCountRef = useRef(null);

	// После оплаты: вызываем confirm-payment для моментального подтверждения, затем polling
	useEffect(() => {
		const paymentFlag = localStorage.getItem('payment_success');
		const urlPaymentSuccess = searchParams.get('payment_success');
		const urlOrderId = searchParams.get('order_id');
		const isPaymentReturn = (paymentFlag === 'true' || urlPaymentSuccess === '1') && showPurchases && !userId;
		if (isPaymentReturn) {
			const savedOrderId = localStorage.getItem('payment_order_id') || urlOrderId;
			localStorage.removeItem('payment_success');
			localStorage.removeItem('payment_data');
			localStorage.removeItem('payment_order_id');
			// Убираем query params из URL чтобы refresh не вызвал повторное подтверждение
			if (urlPaymentSuccess) {
				searchParams.delete('payment_success');
				searchParams.delete('order_id');
				setSearchParams(searchParams, { replace: true });
			}

			// Моментально очищаем корзину в Redux + блокируем fetchCart от перезаписи
			dispatch(clearCart());
			dispatch(setPaymentClearing(true));

			setActiveCategory('purchases');
			setPollingPayment(true);
			initialCountRef.current = purchasedProducts?.count ?? purchasedProducts?.results?.length ?? 0;

			// Подтверждаем оплату на бэкенде (не ждём webhook)
			if (savedOrderId) {
				dispatch(confirmPayment(savedOrderId))
					.unwrap()
					.then(() => {
						// Снимаем блокировку и обновляем корзину (уже пустая на сервере)
						dispatch(setPaymentClearing(false));
						dispatch(fetchCart());
						dispatch(fetchPurchasedProducts({ page: 1, pageSize: 24 }));
					})
					.catch((err) => {
						console.error('Failed to confirm payment:', err);
						dispatch(setPaymentClearing(false));
						dispatch(fetchCart());
						dispatch(fetchPurchasedProducts({ page: 1, pageSize: 24 }));
					});
			} else {
				// Нет orderId — снимаем блокировку
				dispatch(setPaymentClearing(false));
			}

			// Polling как fallback (если confirm не сработал или webhook задерживается)
			let attempts = 0;
			const maxAttempts = 10;
			pollingRef.current = setInterval(() => {
				attempts++;
				dispatch(fetchPurchasedProducts({ page: 1, pageSize: 24 }));
				if (attempts >= maxAttempts) {
					clearInterval(pollingRef.current);
					setPollingPayment(false);
				}
			}, 3000);
		}
		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
		};
	}, []);

	// Остановка polling когда количество продуктов увеличилось
	useEffect(() => {
		if (!pollingPayment || initialCountRef.current === null) return;
		const currentCount = purchasedProducts?.count ?? purchasedProducts?.results?.length ?? 0;
		if (currentCount > initialCountRef.current) {
			clearInterval(pollingRef.current);
			setPollingPayment(false);
		}
	}, [purchasedProducts, pollingPayment]);

	const [page, setPage] = useState(1);
	const pageSize = 48;

	const handleCategoryChange = (category) => {
		setActiveCategory(category);
		// Reset pagination to first page when switching category to avoid out-of-range (404)
		setPage(1);
	};

	// Load purchased products (для My purchases)
	useEffect(() => {
		if (showPurchases) {
			dispatch(fetchPurchasedProducts({ page, pageSize }));
		}
	}, [dispatch, showPurchases, page, pageSize]);

	// Preload all assets for client-side pagination in Assets
	useEffect(() => {
		const loadAllAssets = async () => {
			setAllAssetsLoading(true);
			setAllAssetsError(null);
			try {
				const base = `${BASE_URL}/products/v1/user-products/`;
				const url = userId
					? `${base}?page=1&page_size=1000&user_id=${userId}`
					: `${base}?page=1&page_size=1000`;
				// Для чужого профиля используем публичный запрос, для своего - авторизованный
				const res = userId
					? await fetch(url, { method: 'GET' })
					: await fetchWithAuth(url, { method: 'GET' });

				if (!res.ok) {
					const errorDetails = await readErrorResponse(res);
					setAllAuthorAssets(null);
					setAllAssetsError(extractErrorMessage(errorDetails));
					return;
				}

				const data = await res.json();
				setAllAuthorAssets(data);
			} catch (error) {
				setAllAuthorAssets(null);
				setAllAssetsError(error.message || "Failed to load products.");
			} finally {
				setAllAssetsLoading(false);
			}
		};
		
		// Загружаем все ассеты при инициализации для текущего пользователя
		if (!userId) {
			loadAllAssets();
		} else {
			// Для чужих профилей загружаем только при переключении на Assets
			if (activeCategory === 'assets') {
				loadAllAssets();
			} else {
				setAllAuthorAssets(null);
				setAllAssetsError(null);
			}
		}
	}, [activeCategory, userId]);

	// Load server-paginated data for other users' assets
	useEffect(() => {
		if (userId && activeCategory === 'assets') {
			dispatch(fetchUserProducts({ page, pageSize, userId }));
		}
	}, [userId, dispatch, page, pageSize, activeCategory]);

	// Determine which list to show
	let currentProducts = activeCategory === 'purchases' ? purchasedProducts : userProducts;
	
	// If it's someone else's profile and purchases are hidden, show only their assets
	let displayProducts = userId && !showPurchases ? userProducts : currentProducts;

	// На вкладке Assets: не показываем стейлые userProducts из Redux,
	// ждём загрузки allAuthorAssets (для любого профиля)
	if (activeCategory === 'assets' && !allAuthorAssets) {
		displayProducts = null;
	}

	// Для вкладки Assets используем client-side пагинацию из предварительно загруженных данных
	if (activeCategory === 'assets' && allAuthorAssets && Array.isArray(allAuthorAssets.results)) {
		const safeResults = allAuthorAssets.results;
		const totalCount = safeResults.length;
		const startIndex = (page - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		const slice = safeResults.slice(startIndex, endIndex);
		const totalPages = Math.ceil(totalCount / pageSize);
		displayProducts = {
			results: slice,
			count: totalCount,
			next: page < totalPages ? '#' : null,
			previous: page > 1 ? '#' : null,
		};
	}

	const tabsJsx = (
		<div className="assets-toggle">
			{showPurchases && (
				<button
					className={`asset-category ${activeCategory === 'purchases' ? 'active' : ''}`}
					onClick={() => handleCategoryChange('purchases')}
				>
					My purchases
				</button>
			)}
			{userId && !showPurchases ? (
				<span className="asset-category" style={{ color: '#000', textDecoration: 'none', cursor: 'default' }}>Products</span>
			) : (
				<button
					className={`asset-category ${activeCategory === 'assets' ? 'active' : ''}`}
					onClick={() => handleCategoryChange('assets')}
				>
					My products
				</button>
			)}
		</div>
	);

	let contentJsx;
	if (activeCategory === 'assets' && allAssetsError) {
		contentJsx = (
			<div style={{ paddingTop: '50px', paddingBottom: '50px', minHeight: '300px' }}>
				<h2 className="h2-medium" style={{ fontSize: '40px' }}>{allAssetsError}</h2>
			</div>
		);
	} else if ((allAssetsLoading || !displayProducts) && activeCategory === 'assets' && !allAuthorAssets) {
		contentJsx = (
			<div style={{ paddingTop: '50px', paddingBottom: '50px', minHeight: '400px' }}>
				<h2 className="h2-medium" style={{ fontSize: '40px' }}>Loading...</h2>
			</div>
		);
	} else if (displayProducts?.results && displayProducts.results.length > 0) {
		contentJsx = (
			<AssetsGrid
				items={displayProducts.results.map(item => ({...item, is_test_force_purchased: !!userId}))}
				pagination={{
					count: displayProducts.count,
					next: displayProducts.next,
					previous: displayProducts.previous
				}}
				currentPage={page}
				onPageChange={setPage}
				customPageSize={pageSize}
			/>
		);
	} else if (displayProducts?.results && displayProducts.results.length === 0) {
		let message;
		if (activeCategory === 'purchases') {
			message = "You haven't purchased any products yet";
		} else if (userId) {
			message = "Author has no products yet";
		} else {
			message = "You haven't created any products yet";
		}
		contentJsx = (
			<div style={{ paddingTop: '50px', paddingBottom: '50px', minHeight: '300px' }}>
				<h2 className="h2-medium" style={{ fontSize: '40px' }}>{message}</h2>
			</div>
		);
	} else {
		contentJsx = (
			<div style={{ paddingTop: '50px', paddingBottom: '50px', minHeight: '400px' }}>
				<h2 className="h2-medium" style={{ fontSize: '40px' }}>Loading...</h2>
			</div>
		);
	}

	return (
		<section className="width">
			{tabsJsx}
			{contentJsx}
		</section>
	);
};

export default ProfileAssets;
